"use client";

import EmptyState from "@/components/portfolio/empty-state";
import { useParseText } from "@/hooks/useParseText";
import { cn } from "@/lib/utils";
import { get } from "@/utils/utils";
import { ChevronUp } from "lucide-react";
import { useState } from "react";

interface AboutTabContentProps {
    guess: any;
    event: any;
    isSportEvent?: boolean;
}

export function AboutTabContent( { guess, event, isSportEvent = false }: AboutTabContentProps ) {
    const [ isRuleExpanded, setIsRuleExpanded ] = useState( true );
    const [ isTimelineExpanded, setIsTimelineExpanded ] = useState( true );

    // 从 guess 中获取规则内容
    const ruleContent = get( guess, "rules", "" );
    const { renderParts } = useParseText( ruleContent );

    // 使用 status 判断当前状态
    // status: 1=激活第一个, 2=激活第二个, 3=激活第三个
    const status = get( guess, "status", 1 );
    const currentStep = status;

    // 获取结果标签（最后一个节点显示）
    // result 为 1 显示 yesDesc，result 为 2 显示 noDesc
    const resultValue = get( guess, "result", 0 );
    const resolvedResult = resultValue === 1
        ? get( guess, "yesDesc", "" )
        : resultValue === 2
        ? get( guess, "noDesc", "" )
        : "";

    return (
        <div className={isSportEvent ? "p-3" : "pt-3 -mb-3"}>
            {/* Rule 区域 */}
            <div>
                <h3 className="text-sm font-[600] text-t1 mb-2">Rule</h3>
                {ruleContent
                    ? <p className="text-sm font-[400] text-t1 leading-[22px]">{renderParts()}</p>
                    : <EmptyState text="No rules available." className="h-auto py-3" textClassName="text-sm" />}
            </div>

            {/* Timeline and payout 区域 */}
            <div>
                <button
                    onClick={() => setIsTimelineExpanded( !isTimelineExpanded )}
                    className="flex items-center justify-between w-full py-3"
                >
                    <div className="flex items-center gap-2">
                        {/* 日历图标 */}
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="size-6"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                        >
                            <path
                                d="M7 11H9V13H7V11ZM21 6V20C21 21.1 20.1 22 19 22H5C3.89 22 3 21.1 3 20L3.01 6C3.01 4.9 3.89 4 5 4H6V2H8V4H16V2H18V4H19C20.1 4 21 4.9 21 6ZM5 8H19V6H5V8ZM19 20V10H5V20H19ZM15 13H17V11H15V13ZM11 13H13V11H11V13Z"
                                fill="var(--ui-content-primary)"
                                fillOpacity="0.9"
                            />
                        </svg>
                        <span className="text-sm font-[500] text-t1">Timeline and payout</span>
                    </div>
                    <ChevronUp
                        className={`w-5 h-5 text-t3 transition-transform duration-200 ${
                            isTimelineExpanded ? "" : "rotate-180"
                        }`}
                    />
                </button>

                {/* 时间轴内容 */}
                <div
                    className={`overflow-hidden transition-all duration-200 ${
                        isTimelineExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
                    }`}
                >
                    {/* Market open */}
                    <TimelineItem
                        isActive={currentStep >= 1}
                        isLast={false}
                        title="Market open"
                        content="Only when an outcome is reached"
                    />

                    {/* Market closes/closed */}
                    <TimelineItem
                        isActive={currentStep >= 2}
                        isLast={false}
                        title={currentStep >= 2 ? "Market closed" : "Market closes"}
                        content="The guessing game is under maintenance."
                    />

                    {/* Resolution */}
                    <TimelineItem
                        isActive={currentStep >= 3}
                        isLast={true}
                        title={currentStep >= 3 ? "Resolved" : "Resolution"}
                        content={currentStep >= 3
                            ? ""
                            : "The outcome will be validated by the team within 24 hours of its occurrence"}
                        resultLabel={currentStep >= 3 ? resolvedResult : undefined}
                    />
                </div>
            </div>
        </div>
    );
}

function TimelineItem(
    { isActive, isLast, title, content, resultLabel }: {
        isActive: boolean;
        isLast: boolean;
        title: string;
        content: string;
        resultLabel?: string;
    },
) {
    return (
        <div className="flex">
            {/* 左侧：圆点和连接线容器，固定宽度保证对齐 */}
            <div className="w-6 mr-3 flex-shrink-0">
                {/* 圆点容器 - 统一高度保证对齐 */}
                <div className="h-5 flex items-center justify-center">
                    {isActive
                        ? (
                            <div className="size-5 flex items-center justify-center">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    className="size-5"
                                >
                                    <path
                                        d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 15L3 10L4.41 8.59L8 12.17L15.59 4.58L17 6L8 15Z"
                                        fill="#09C285"
                                    />
                                </svg>
                            </div>
                        )
                        : <div className="w-2.5 h-2.5 rounded-full bg-t3" />}
                </div>
                {/* 连接线 - 只有非最后一个才显示 */}
                {!isLast && (
                    <div className="flex justify-center">
                        <div
                            className={cn( "w-0.5 min-h-10", isActive ? "" : "bg-t3" )}
                            style={{ backgroundColor: isActive ? "#09C285" : undefined }}
                        />
                    </div>
                )}
            </div>

            {/* 右侧：内容 */}
            <div className="pb-4 flex flex-col justify-center min-h-6">
                <div className="text-sm font-[400] text-t1 leading-6">{title}</div>
                {content && <div className="text-xs font-[400] text-t2 mt-1">{content}</div>}
                {/* 结果标签 */}
                {resultLabel && (
                    <div className="mt-2 w-[46px] h-6 rounded flex items-center justify-center text-xs font-[500] bg-action-accent text-content-fixed-inverse">
                        {resultLabel}
                    </div>
                )}
            </div>
        </div>
    );
}
